// S-DM-005 — the SPA's half of the typed-refusal contract (S-DM-003,
// src/RepoLens.Shared/Models/ToolResult.cs).
//
// THE BUG THIS FIXES. A refusal arrives as `{ success: false, error: {...} }`.
// SearchPage used to fall through to `nodes = result.data ?? []`, so a refusal
// rendered as "no results found" — the user was told their query matched
// nothing when in fact the platform declined to answer and said exactly how to
// narrow. This module turns that response into a distinct, actionable state.
//
// CONTRACT RULE: branch on `code`, NEVER on `message`. The message is display
// prose and is free to be reworded at any time; the SCREAMING_SNAKE code is the
// contract. There is deliberately no string matching on `message` anywhere in
// this file.

/**
 * Codes that mean "the platform is healthy and you can fix this by re-asking".
 * Mirrors every `ToolErrorCode` member EXCEPT `INTERNAL_ERROR`.
 */
export const REFUSAL_CODES = [
  'REPOSITORY_FORMAT_INVALID',
  'SYMBOL_PREFIX_TOO_SHORT',
  'FILE_PATH_REQUIRED',
  'NODE_ID_REQUIRED',
  'NODE_NOT_FOUND',
  'UNKNOWN_TOOL',
  'SCOPE_REQUIRED',
] as const;

export type RefusalCode = (typeof REFUSAL_CODES)[number];

/**
 * NOT a refusal. `INTERNAL_ERROR` means RepoLens has a bug: no argument the
 * caller changes will help, so it must never be presented as a scope panel
 * offering a way to narrow.
 */
export const PLATFORM_ERROR_CODE = 'INTERNAL_ERROR';

/** One value of one dimension, with how many corpus items carry it. */
export interface FacetValue {
  value: string;
  count: number | null;
}

/**
 * One facet dimension and its values, exactly as the refusal carried them.
 *
 * S-DM-005 AC6 (EXP-DM-5): the SPA used to read the `repository` dimension ONLY,
 * so the unknown-branch refusal — the only facet-bearing refusal the platform
 * actually emits — rendered zero clickable controls and the valid branches
 * survived only as prose. Every dimension is kept here, and `ScopePanel`
 * renders a control per dimension, so the NEXT dimension ADR-012 adds
 * (organization, language, kind) needs no frontend change.
 */
export interface RefusalFacet {
  /** Wire dimension name, lowercased: `repository`, `branch`, … */
  dimension: string;
  values: FacetValue[];
}

/** The ADR-012 §6.2 counts/tier/top-N payload, when the refusal carried one. */
export interface ScopePayload {
  totalCount: number | null;
  isApproximate: boolean;
  cardinalityTier: string | null;
  topMatches: FacetValue[];
}

export interface RefusalFailure {
  kind: 'refusal';
  /** The wire code when it is one we know; `null` for an unrecognised code. */
  code: RefusalCode | null;
  /** The raw wire code, whatever it was — kept for display/diagnostics. */
  rawCode: string;
  /** False when the code is absent or outside the set this build knows. */
  recognized: boolean;
  message: string;
  /** Dimensions that would make the request answerable. */
  dimensions: string[];
  /**
   * EVERY facet dimension the refusal carried, in wire order — each becomes a
   * clickable narrowing control (S-DM-005 AC6). Facets with no usable values
   * are dropped, so this list never produces an empty region.
   */
  facets: RefusalFacet[];
  /**
   * Repository-dimension facet values. Retained unchanged for backward
   * compatibility — a `repository` facet must behave exactly as it did before
   * AC6 generalized the rendering. Equivalent to the `repository` entry of
   * {@link facets}.
   */
  repositoryFacets: FacetValue[];
  scope: ScopePayload | null;
}

export interface PlatformFailure {
  kind: 'platform-error';
  /** User-facing copy. Deliberately generic — see `detail`. */
  message: string;
  /**
   * The server's own prose for the bug, kept for diagnostics only. It is a
   * .NET exception message, not guidance, so it is logged rather than shown.
   */
  detail: string;
}

export type ToolFailure = RefusalFailure | PlatformFailure;

const GENERIC_REFUSAL_MESSAGE = 'RepoLens declined to answer this request.';
const GENERIC_PLATFORM_MESSAGE = 'Something went wrong on our side — this is not your query.';

/** Read a property under either camelCase or PascalCase, so a serializer-casing
 *  change cannot silently blank the panel. */
function pick(obj: Record<string, unknown>, camel: string): unknown {
  if (camel in obj) return obj[camel];
  const pascal = camel.charAt(0).toUpperCase() + camel.slice(1);
  return pascal in obj ? obj[pascal] : undefined;
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

function asString(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function asCount(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function asStringList(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string' && s.trim() !== '') : [];
}

function asFacetValues(v: unknown): FacetValue[] {
  if (!Array.isArray(v)) return [];
  return v
    .map((raw) => {
      const rec = asRecord(raw);
      if (!rec) return null;
      const value = asString(pick(rec, 'value')).trim();
      if (!value) return null;
      return { value, count: asCount(pick(rec, 'count')) };
    })
    .filter((f): f is FacetValue => f !== null);
}

/**
 * Pull out EVERY facet dimension the refusal carried (S-DM-005 AC6). A facet
 * with an unreadable shape, a blank dimension name, or no usable values is
 * dropped, so a malformed payload can never produce a region with no controls.
 */
export function extractFacets(error: unknown): RefusalFacet[] {
  const rec = asRecord(error);
  if (!rec) return [];
  const facets = pick(rec, 'facets');
  if (!Array.isArray(facets)) return [];
  const out: RefusalFacet[] = [];
  for (const raw of facets) {
    const facet = asRecord(raw);
    if (!facet) continue;
    const dimension = asString(pick(facet, 'dimension')).trim().toLowerCase();
    if (!dimension) continue;
    const values = asFacetValues(pick(facet, 'values'));
    if (values.length === 0) continue;
    out.push({ dimension, values });
  }
  return out;
}

/** Pull out the values of the `repository` facet, if the refusal carried one. */
export function extractRepositoryFacets(error: unknown): FacetValue[] {
  return extractFacets(error)
    .filter((f) => f.dimension === 'repository')
    .flatMap((f) => f.values);
}

function extractScope(error: Record<string, unknown>): ScopePayload | null {
  const scope = asRecord(pick(error, 'scope'));
  if (!scope) return null;
  return {
    totalCount: asCount(pick(scope, 'totalCount')),
    isApproximate: pick(scope, 'isApproximate') === true,
    cardinalityTier: asString(pick(scope, 'cardinalityTier')) || null,
    topMatches: asFacetValues(pick(scope, 'topMatches')),
  };
}

function isRefusalCode(code: string): code is RefusalCode {
  return (REFUSAL_CODES as readonly string[]).includes(code);
}

/**
 * Classify the `error` of a `{ success: false, ... }` response.
 *
 * The ONLY input to the branch is `code`. Three outcomes:
 *  - `INTERNAL_ERROR` → `platform-error` (a bug, not a refusal).
 *  - a code in {@link REFUSAL_CODES} → `refusal`, `recognized: true`.
 *  - anything else (unknown code, missing code, legacy bare-string error) →
 *    `refusal` with `recognized: false`: the message is still shown, so an
 *    unfamiliar code degrades to plain prose rather than a crash or a blank
 *    screen. A refusal with no usable error object at all is the one case
 *    reported as a platform error, since it hands the user nothing to act on.
 */
export function classifyToolFailure(error: unknown): ToolFailure {
  // Legacy/back-compat: the pre-S-DM-003 wire form was a bare string.
  if (typeof error === 'string') {
    const message = error.trim();
    return message
      ? {
          kind: 'refusal',
          code: null,
          rawCode: '',
          recognized: false,
          message,
          dimensions: [],
          facets: [],
          repositoryFacets: [],
          scope: null,
        }
      : { kind: 'platform-error', message: GENERIC_PLATFORM_MESSAGE, detail: '' };
  }

  const rec = asRecord(error);
  if (!rec) {
    // success:false with no error payload — nothing actionable exists.
    return { kind: 'platform-error', message: GENERIC_PLATFORM_MESSAGE, detail: '' };
  }

  const rawCode = asString(pick(rec, 'code')).trim();
  const message = asString(pick(rec, 'message')).trim();

  if (rawCode === PLATFORM_ERROR_CODE) {
    // The user-facing copy is generic; the server's exception prose is not
    // guidance and is carried as `detail` for the console instead.
    return { kind: 'platform-error', message: GENERIC_PLATFORM_MESSAGE, detail: message };
  }

  const recognized = isRefusalCode(rawCode);
  return {
    kind: 'refusal',
    code: recognized ? rawCode : null,
    rawCode,
    recognized,
    message: message || GENERIC_REFUSAL_MESSAGE,
    dimensions: asStringList(pick(rec, 'dimensions')),
    facets: extractFacets(rec),
    repositoryFacets: extractRepositoryFacets(rec),
    scope: extractScope(rec),
  };
}

/**
 * AC3 — the frozen shareable URL contract (`?q=`/`?repo=`/`?branch=`), and no
 * cursor: a facet click starts a fresh narrowed search, it does not resume a
 * previous page.
 */
/**
 * S-DM-005 AC6 — the dimensions a facet click can actually narrow ON, which is
 * bounded by the frozen shareable URL contract (`?q=`/`?repo=`/`?branch=`).
 * A facet in any other dimension gets no control and the panel says why, rather
 * than rendering a button that cannot change the query.
 */
export const NARROWABLE_DIMENSIONS = ['repository', 'branch'] as const;

export type NarrowableDimension = (typeof NARROWABLE_DIMENSIONS)[number];

export function isNarrowableDimension(dimension: string): dimension is NarrowableDimension {
  return (NARROWABLE_DIMENSIONS as readonly string[]).includes(dimension.trim().toLowerCase());
}

/** The repository/branch scope a search runs under. */
export interface ScopeSelection {
  repo: string;
  branch: string;
}

/**
 * DIMENSION-AWARE NARROWING (S-DM-005 AC6). Apply a facet click to the current
 * scope: a `repository` facet sets the repository, a `branch` facet sets the
 * branch. Returns `null` for a dimension this build cannot narrow on, so the
 * caller can decline rather than silently re-run the identical search.
 *
 * BACKWARD COMPATIBILITY: the `repository` case reproduces the pre-AC6
 * behaviour exactly — repository set to the clicked value, branch CLEARED,
 * because the branch the user had selected belongs to the repository they were
 * scoped to before, not the one they just clicked.
 */
export function applyFacetSelection(
  dimension: string,
  value: string,
  current: ScopeSelection,
): ScopeSelection | null {
  switch (dimension.trim().toLowerCase()) {
    case 'repository':
      return { repo: value, branch: '' };
    case 'branch':
      return { repo: current.repo, branch: value };
    default:
      return null;
  }
}

/** Human label for a facet dimension's region heading. */
export function facetDimensionLabel(dimension: string): string {
  switch (dimension) {
    case 'repository':
      return 'Repositories';
    case 'branch':
      return 'Branches';
    default:
      return dimension.charAt(0).toUpperCase() + dimension.slice(1);
  }
}

export function buildNarrowedSearchUrl(q: string, repo: string, branch: string): string {
  const params = new URLSearchParams();
  if (q.trim()) params.set('q', q.trim());
  if (repo.trim()) params.set('repo', repo.trim());
  if (branch.trim()) params.set('branch', branch.trim());
  return `/search?${params.toString()}`;
}
